import { useMutation } from '@apollo/client'
import Hls from 'hls.js'
import throttle from 'lodash.throttle'
import { FC, useCallback, useEffect, useMemo, useRef } from 'react'
import { isMobile } from 'react-device-detect'
import { UpdateViwewingTimeDocument } from '~/__generated__/graphql'
import { useVideoLanguage } from '~/hooks/useGetVideoLanguage'
import browserEnv from '~/libs/env/browserEnv'
import * as GA from '~/libs/ga'
import { getVideoWatchedPercent } from '~/libs/video/getVideoWatchedPercent'
import { useVideoDetail } from '../../hooks/useVideoDetail'
import { useVideoEventHandler } from '../VideoEventHandlerProvider'

const VideoPlayer: FC<{ id: string }> = ({ id }) => {
  const { data, cookie, refetch } = useVideoDetail(id)
  const [updateViewingTime] = useMutation(UpdateViwewingTimeDocument, {
    ignoreResults: true,
  })
  const video = data?.originalVideo
  const lambda = browserEnv.NEXT_PUBLIC_PLAYER_PROXY_LAMBDA
  const videoRef = useRef<HTMLVideoElement | null>(null)

  const isHlsSupported = useMemo(() => Hls.isSupported(), [])

  const isReady = useRef(false)
  const timeUpdateEvents = useRef<Set<number>>(new Set())
  const lapsedSec = useRef<number>(0)

  const didSendWatchComplete = useRef(false)

  const sendVideoInsightsEvent = useCallback(
    (action: string, value?: string) => {
      if (isReady.current) {
        GA.sendEvent({
          event: 'video_insights',
          screen: 'movie_page',
          category: 'player',
          action,
          label: `${video?.id}`,
          value,
        })
      }
    },
    [video?.id],
  )

  const patchViewing = useCallback(
    (event: Event) => {
      if (event.target instanceof HTMLVideoElement) {
        const seconds = event.target.currentTime
        if (seconds == 0) return
        const viewingMin = Math.floor(seconds / 60.0)
        const viewingSecond = Math.floor(seconds % 60.0)
        updateViewingTime({
          variables: {
            videoId: id,
            viewingMin,
            viewingSecond,
          },
        })
      }
    },
    [id, updateViewingTime],
  )
  const patchViewingOnTimeUpdate = useMemo(() => throttle(patchViewing, 10000), [patchViewing])

  const onVolumeChange = useMemo(
    () =>
      throttle((event: Event) => {
        sendVideoInsightsEvent('video_views_volume_change')
      }, 3000),
    [sendVideoInsightsEvent],
  )

  const onTimeUpdate = useMemo(
    () =>
      throttle((event: Event) => {
        if (!isReady.current) return
        if (lapsedSec.current >= 1 && !timeUpdateEvents.current.has(1)) {
          sendVideoInsightsEvent('video_views_time_watched_second')
          timeUpdateEvents.current.add(1)
        }
        if (lapsedSec.current >= 3 && !timeUpdateEvents.current.has(3)) {
          sendVideoInsightsEvent('video_views_3s_watched_count')
          timeUpdateEvents.current.add(3)
        }
        if (lapsedSec.current >= 5 && !timeUpdateEvents.current.has(5)) {
          sendVideoInsightsEvent('video_views_5s_watched_count')
          timeUpdateEvents.current.add(5)
        }
        if (lapsedSec.current >= 10 && !timeUpdateEvents.current.has(10)) {
          sendVideoInsightsEvent('video_views_10s_watched_count')
          timeUpdateEvents.current.add(10)
        }
        if (lapsedSec.current >= 15 && !timeUpdateEvents.current.has(15)) {
          sendVideoInsightsEvent('video_views_15s_watched_count')
          timeUpdateEvents.current.add(15)
        }
        if (lapsedSec.current >= 30 && !timeUpdateEvents.current.has(30)) {
          sendVideoInsightsEvent('video_views_30s_watched_count')
          timeUpdateEvents.current.add(30)
        }
        lapsedSec.current += 1

        const video = event.target as HTMLVideoElement
        if (
          !didSendWatchComplete.current &&
          video != null &&
          video.currentTime / video.duration > 0.9
        ) {
          sendVideoInsightsEvent('video_views_complete_watched_count')
          didSendWatchComplete.current = true
        }
      }, 1000),
    [sendVideoInsightsEvent],
  )

  const { addEventListeners } = useVideoEventHandler({
    onVideoPlay(event) {
      sendVideoInsightsEvent('video_views_play_count')
    },
    onVideoPause(event) {
      sendVideoInsightsEvent('video_views_stop_count')
    },
    onVolumeChange(event) {
      onVolumeChange(event)
    },
    onEnterPip(event) {
      sendVideoInsightsEvent('video_views_pip_on')
    },
    onLeavePip(event) {
      sendVideoInsightsEvent('video_views_pip_off')
    },
    onRateChange(event) {
      const videoElem = event.target
      if (!(videoElem instanceof HTMLVideoElement)) {
        return
      }
      sendVideoInsightsEvent('video_views_speed_value', `${videoElem.playbackRate}`)
    },
    onTimeUpdate(event) {
      patchViewingOnTimeUpdate(event)
      onTimeUpdate(event)
    },
    onEnded(event) {
      sendVideoInsightsEvent('video_views_complete_watched_count')
    },
    onCanPlay(event) {
      const videoElem = event.target
      if (!(videoElem instanceof HTMLVideoElement)) {
        return
      }
      if (!videoElem.src.endsWith('blank.mp4')) {
        isReady.current = true
      } else {
        isReady.current = false
      }
    },
  })

  const { getVideoLanguage, updateVideoLanguage } = useVideoLanguage()

  const limitTime = useMemo(() => {
    if (!cookie) return undefined

    const policy = cookie
      .split('&')
      .find((part) => part.startsWith('Policy='))
      ?.split('=')[1]
    if (!policy) return undefined

    try {
      const decodedPolicy = Buffer.from(policy.slice(0, -1), 'base64').toString('utf-8') // policyの末尾の__を削除
      const limitTime = JSON.parse(decodedPolicy)['Statement'][0]['Condition']['DateLessThan'][
        'AWS:EpochTime'
      ] as number

      return limitTime
    } catch (e) {
      console.error('Failed to decode policy', e)
    }
  }, [cookie])

  // 毎秒cookieの有効期限が切れてないか確認
  useEffect(() => {
    const checkLimit = setInterval(() => {
      if (limitTime && Math.floor(Date.now() / 1000) > limitTime) {
        refetch().catch((e) => {
          console.error('Failed to refetch cookie', e)
        })
      }
    }, 1000)

    return () => clearInterval(checkLimit)
  })

  useEffect(() => {
    if (!video?.fileUrl) return
    const videoElem = videoRef.current
    if (!videoElem) return

    const src = video.fileUrl ?? ''
    const q = cookie
    const usesHls = isHlsSupported && !isMobile

    if (usesHls) {
      const hls = new Hls()
      if (q !== undefined) {
        hls.config.xhrSetup = (xhr, url) => {
          xhr.open('GET', `${url}?${q}`, true)
        }
      }
      hls.loadSource(video.fileUrl)
      hls.attachMedia(videoElem)
    } else if (videoElem.canPlayType('application/vnd.apple.mpegurl')) {
      // SafariなどのHls対応しているブラウザ用
      if (q !== undefined && lambda !== undefined) {
        const parsedUrl = new URL(src)
        const host = parsedUrl.hostname
        const pathComponents = parsedUrl.pathname.split('/')
        const id = pathComponents[2]
        const filename = pathComponents[3]
        videoElem.src = `${lambda}/${id}/${filename}?${q}&Origin-Host=${host}`
      } else {
        videoElem.src = src
      }

      videoElem.setAttribute('playsInline', 'true')
      videoElem.load()
    } else {
      console.error(
        'This is an old browser that does not support MSE https://developer.mozilla.org/en-US/docs/Web/API/Media_Source_Extensions_API',
      )
    }

    addEventListeners?.(videoElem)
  }, [video?.fileUrl, id, cookie, lambda, isHlsSupported, addEventListeners])

  const videoLanguage = useMemo(
    () => (video?.vtts ? getVideoLanguage(video.vtts) : 'disabled'),
    [video?.vtts, getVideoLanguage],
  )

  useEffect(() => {
    const videoCurrent = videoRef.current

    const handleMetadataLoaded = () => {
      if (videoCurrent) {
        if (video?.viewingTime) {
          const watchedPercent = getVideoWatchedPercent(
            {
              minutes: video?.duration?.minutes ?? undefined,
              seconds: video?.duration?.seconds ?? undefined,
            },
            {
              minutes: video.viewingTime.viewingMin ?? undefined,
              seconds: video.viewingTime.viewingSecond ?? undefined,
            },
          )
          // 95%以上視聴済みの場合は、最初から再生する
          if (watchedPercent && watchedPercent >= 0.95) {
            videoCurrent.currentTime = 0
          } else {
            videoCurrent.currentTime =
              (video?.viewingTime?.viewingMin ?? 0) * 60 + (video?.viewingTime?.viewingSecond ?? 0)
          }

          videoCurrent.play().catch((err) => {
            console.error('Autoplay failed:', err)
          })
        }

        // modeをhiddenにすることで字幕の読み込みを行う
        Array.from(videoCurrent.textTracks).forEach((track) => (track.mode = 'hidden'))

        // cuesが読み込まれたら字幕を表示
        const checkCuesLoaded = () => {
          if (
            videoCurrent.textTracks.length === video?.vtts?.length &&
            Array.from(videoCurrent.textTracks).every(
              (track) => track.cues && track.cues.length > 0,
            )
          ) {
            Array.from(videoCurrent.textTracks).forEach(
              (track) => (track.mode = track.language === videoLanguage ? 'showing' : 'hidden'),
            )

            videoCurrent.textTracks.onchange = () => {
              const activeTrack = Array.from(videoCurrent.textTracks).find(
                (track) => track.mode === 'showing',
              )
              updateVideoLanguage(activeTrack?.language ?? 'disabled')
            }
          } else {
            setTimeout(checkCuesLoaded, 100)
          }
        }

        checkCuesLoaded()
      }
    }

    if (videoCurrent) {
      videoCurrent.addEventListener('loadedmetadata', handleMetadataLoaded)
    }

    return () => {
      if (videoCurrent) {
        videoCurrent.removeEventListener('loadedmetadata', handleMetadataLoaded)
      }
    }
  }, [updateVideoLanguage, video, videoLanguage])

  return (
    <video ref={videoRef} style={{ width: '100%' }} controls>
      {video?.vtts?.map((vtt) => (
        <track
          key={vtt.locale}
          src={`/proxy_resource${new URL(vtt.url).pathname}`}
          kind='captions'
          srcLang={vtt.locale}
          label={vtt.label}
        />
      ))}
    </video>
  )
}

export default VideoPlayer
